import { checkIsInternal, getTinGroupId } from '@src/utility/createStorage';
import HttpService from '@src/utility/httpService';
import { config } from '@uhg-abyss/web/tools/config';
import type { Timeout } from 'node:timers';

const _httpService = new HttpService();

const apiDomainInternal = config('API_DOMAIN_INTERNAL') ?? '';
const apiDomainExternal = config('API_DOMAIN_EXTERNAL') ?? '';

if (checkIsInternal()) {
  _httpService.setBaseURL(apiDomainInternal);
} else {
  _httpService.setBaseURL(apiDomainExternal);
}

/**
 * Interface for job status polling pollingData
 */
interface JobStatusResponse {
  Status: string;
  StatusCode: number;
  Message: string;
  Data: {
    JobId: string;
    Status: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
    Message: string;
    Result: unknown;
  };
  TimeStamp: number;
}

/**
 * React-friendly polling function that accepts state setters
 * @param jobId - The job ID to poll
 * @param callbacks - Object containing callback functions for state updates
 * @param apiEndpoint - The API endpoint (default: '/job-status/result')
 * @param pollingInterval - Interval in milliseconds (default: 1000)
 * @param requestBody - Optional request body to send with polling requests
 * @param useMessageCheck - If true, checks for "Successfully Data Retrieved" message instead of Status field
 */
export const startJobPolling = (
  jobId: string,
  callbacks: {
    onInProgress?: () => void;
    onCompleted?: (result: unknown) => void;
    onFailed?: (error: string) => void;
    onStatusUpdate?: (response: JobStatusResponse) => void;
    onStop?: (stopFn: () => void) => void;
  },
  apiEndpoint: string = '/job-status/result',
  pollingInterval: number = 1000,
  requestBody?: unknown,
  useMessageCheck: boolean = false
): (() => void) => {
  // ============================================================
  // STRICTLY USES BULK PROCESSING API FOR ALL POLLING
  // URL: https://uhcinsights-bulk-process-dev.uhc.com
  // This ensures job status polling always uses the correct endpoint
  // ============================================================
  const bulkProcessingURL = config('BULK_PROCESSING_API') || '';

  // Set the base URL to the bulk processing API
  _httpService.setBaseURL(bulkProcessingURL);

  let intervalId: NodeJS.Timeout | null = null;
  let stopped = false;

  const poll = async () => {
    if (stopped) return;
    try {
      const url = `${apiEndpoint}/${jobId}`;
// Set the base URL to the bulk processing API
      _httpService.setBaseURL(config('BULK_PROCESSING_API') || '');

      // Use POST with request body if provided, otherwise use GET
      const pollingData: JobStatusResponse = requestBody
        ? await _httpService.push(`${url}`, requestBody)
        : await _httpService.get(`${url}`);

// Call the optional status update callback
      if (callbacks.onStatusUpdate) {
        callbacks.onStatusUpdate(pollingData);
      }

      // Determine completion based on useMessageCheck flag
      let isCompleted = false;
      let isFailed = false;

      if (useMessageCheck) {
        // Check message field for completion
        const message = pollingData?.Message || pollingData?.Data?.Message;
        isCompleted = message === 'Successfully Data Retrieved';
        isFailed = message?.toLowerCase().includes('failed');
} else {
        // Use Status field (original behavior)
        isCompleted = pollingData?.Data?.Status === 'COMPLETED';
        isFailed = pollingData?.Data?.Status === 'FAILED';
}

      // Check if job is still in progress
      if (!isCompleted && !isFailed) {
        // Keep polling
        if (callbacks.onInProgress) {
          callbacks.onInProgress();
        }
        return;
      }

      // Job completed or failed - stop polling
      if (intervalId) {
        clearInterval(intervalId);
      }
      stopped = true;

      // Handle completion
      if (isCompleted) {
        if (callbacks.onCompleted) {
          callbacks.onCompleted(
            pollingData.Data?.Result || pollingData.Data || pollingData
          );
        }
      } else if (isFailed) {
        if (callbacks.onFailed) {
          callbacks.onFailed(
            pollingData.Data?.Message || pollingData.Message || 'Job failed'
          );
        }
      }

      // Keep bulk processing URL - don't restore since other polling may be in progress
      // All polling operations should use bulk processing API
    } catch (error) {
      // Stop polling on error
      if (intervalId) {
        clearInterval(intervalId);
      }
      stopped = true;
      if (callbacks.onFailed) {
        callbacks.onFailed(
          error instanceof Error ? error.message : 'Unknown error'
        );
      }
      // Keep bulk processing URL - don't restore since other polling may be in progress
    }
  };

  // Start polling
intervalId = setInterval(poll, pollingInterval);
  poll(); // Call immediately
  // Return cleanup function
  const stopPolling = () => {
    stopped = true;
    if (intervalId) {
      clearInterval(intervalId);
    }
  };
  callbacks.onStop?.(stopPolling);
  return stopPolling;
};

/**
 * Legacy method for backwards compatibility
 * Submits a job to get claims metrics
 */
export const getJobId = async (
  endpoint: string,
  payload: any,
  baseUrl?: string
) => {
  // Set base URL if provided, otherwise use default
  if (baseUrl) {
    _httpService.setBaseURL(baseUrl);
  } else {
    _httpService.setBaseURL(config('BULK_PROCESSING_API') || '');
  }
  const jobId: any = await _httpService.push(`${endpoint}`, payload);
  return jobId;
};

export const getPreviousBaseURL = () => {
  return checkIsInternal()
    ? config('API_DOMAIN_INTERNAL')
    : config('API_DOMAIN_EXTERNAL');
};

// Polling queue system to ensure only one polling job runs at a time
type PollingJob = {
  jobId: string;
  callbacks: {
    onInProgress?: () => void;
    onCompleted?: (result: unknown) => void;
    onFailed?: (error: string) => void;
    onStatusUpdate?: (response: JobStatusResponse) => void;
  };
  apiEndpoint: string;
  pollingInterval: number;
  requestBody?: unknown;
  useMessageCheck: boolean;
  resolve: (cleanup: () => void) => void;
};

const pollingQueue: PollingJob[] = [];
let pollingInProgress = false;
/**
 * Resets the polling queue and unblocks processing.
 * Call this when navigating between pages within the same parcel
 * to clear any stale polling state from a previous page.
 */
export function resetPollingQueue() {
  pollingQueue.length = 0;
  pollingInProgress = false;
}
function processNextPollingJob() {
  if (pollingInProgress || pollingQueue.length === 0) return;
  pollingInProgress = true;
  const {
    jobId,
    callbacks,
    apiEndpoint,
    pollingInterval,
    requestBody,
    useMessageCheck,
    resolve,
  } = pollingQueue.shift()!;

  let cleanup: () => void = () => {};

  cleanup = startJobPolling(
    jobId,
    {
      ...callbacks,
      onCompleted: (result) => {
        callbacks.onCompleted?.(result);
        pollingInProgress = false;
        processNextPollingJob();
      },
      onFailed: (error) => {
        callbacks.onFailed?.(error);
        pollingInProgress = false;
        processNextPollingJob();
      },
    },
    apiEndpoint,
    pollingInterval,
    requestBody,
    useMessageCheck
  );

  resolve(cleanup);
}

export const queueJobPolling = (
  jobId: string,
  callbacks: {
    onInProgress?: () => void;
    onCompleted?: (result: unknown) => void;
    onFailed?: (error: string) => void;
    onStatusUpdate?: (response: JobStatusResponse) => void;
  },
  apiEndpoint: string = '/job-status/result',
  pollingInterval: number = 1000,
  requestBody?: unknown,
  useMessageCheck: boolean = false
): Promise<() => void> => {
  return new Promise((resolve) => {
    pollingQueue.push({
      jobId,
      callbacks,
      apiEndpoint,
      pollingInterval,
      requestBody,
      useMessageCheck,
      resolve,
    });
    processNextPollingJob();
  });
};

// Helper for polling a bulk job
type PollBulkJobOptions = { onStop?: (stopFn: () => void) => void };

const isContractBulkFlow = () =>
  sessionStorage.getItem('hiVolumeContractFlow') === 'true';

export async function pollBulkJob<T = unknown>(
  endpoint: string,
  payload: Record<string, unknown>,
  options?: PollBulkJobOptions
): Promise<T> {
  const updatedPayload = isContractBulkFlow()
    ? payload
    : { TinGroupId: getTinGroupId(), ...payload };
  const jobIdResponse = await getJobId(endpoint, updatedPayload);
  const jobId = jobIdResponse.Data.JobId;
  return new Promise<T>((resolve, reject) => {
    queueJobPolling(
      jobId,
      {
        onInProgress: () => {
},
        onCompleted: (result) => {
resolve(result as T);
        },
        onFailed: (errorMessage) => {
          console.error('Bulk Polling - Job failed:', errorMessage);
          reject(new Error(errorMessage));
        },
        onStatusUpdate: (statusResponse) => {
},
      },
      '/job-status/result',
      1000,
      undefined,
      false
    ).then((cleanup) => {
      if (options && typeof options.onStop === 'function') {
        options.onStop(cleanup);
      }
    });
  });
}

// Duplicate

export async function pollBulkJobParallel<T = unknown>(
  endpoint: string,
  payload: Record<string, unknown>,
  options?: PollBulkJobOptions
): Promise<T> {
  const updatedPayload = isContractBulkFlow()
    ? payload
    : { TinGroupId: getTinGroupId(), ...payload };
  const jobIdResponse = await getJobId(endpoint, updatedPayload);
  const jobId = jobIdResponse.Data.JobId;
  return new Promise<T>((resolve, reject) => {
    const cleanup = startJobPolling(
      jobId,
      {
        onInProgress: () => {
},
        onCompleted: (result) => {
resolve(result as T);
        },
        onFailed: (errorMessage) => {
          console.error('Bulk Polling - Job failed:', errorMessage);
          reject(new Error(errorMessage));
        },
        onStatusUpdate: (statusResponse) => {
},
      },
      '/job-status/result',
      1000,
      undefined,
      false
    );
  });
}